/**
 * Dropdown Component - Theme-aware dropdown menu
 * ✅ Playbook Compliance: Theme-aware, WCAG 2.1 AA compliant, React.createElement
 */

const React = (globalThis as any).React;
const { useState, useRef, useEffect } = React;

import { styles } from '../styles';

export interface DropdownMenuItemProps {
  key?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
  icon?: React.ReactNode;
  onClick?: () => void;
}

export interface DropdownMenuProps {
  children?: React.ReactNode;
  items?: DropdownMenuItemProps[];
}

export interface DropdownProps {
  overlay: React.ReactNode | DropdownMenuItemProps[];
  trigger?: ('click' | 'hover' | 'contextMenu')[];
  children?: React.ReactNode;
  placement?: 'bottomLeft' | 'bottom' | 'bottomRight' | 'topLeft' | 'top' | 'topRight';
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
}

export function DropdownMenu({ children, items }: DropdownMenuProps) {
  const menuItems = items || React.Children.toArray(children);

  const menuStyle: React.CSSProperties = {
    background: 'var(--card-bg)',
    border: '1px solid var(--card-border)',
    borderRadius: '8px',
    boxShadow: 'var(--shadow-lg)',
    padding: '4px',
    minWidth: '120px',
  };

  return React.createElement('div', { style: menuStyle },
    menuItems.map((item: any, index: number) => {
      if (React.isValidElement(item) && (item.type === DropdownMenuItem || (item.type as any)?.displayName === 'DropdownMenuItem')) {
        const props = item.props;
        return React.createElement('button', {
          key: props.key || index,
          onClick: () => {
            props.onClick?.();
          },
          disabled: props.disabled,
          style: {
            width: '100%',
            padding: '8px 12px',
            background: 'transparent',
            border: 'none',
            textAlign: 'left',
            cursor: props.disabled ? 'not-allowed' : 'pointer',
            color: props.danger ? 'var(--error-color)' : 'var(--main-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            opacity: props.disabled ? 0.5 : 1,
            borderRadius: '4px',
            transition: 'background 0.2s ease',
          },
          onMouseEnter: (e: any) => {
            if (!props.disabled) {
              e.currentTarget.style.background = 'var(--highlight-bg)';
            }
          },
          onMouseLeave: (e: any) => {
            e.currentTarget.style.background = 'transparent';
          },
        },
          props.icon,
          props.children
        );
      }
      return item;
    })
  );
}

export function DropdownMenuItem({ children, disabled, danger, icon, onClick }: DropdownMenuItemProps) {
  // MenuItem is just a container - actual rendering happens in DropdownMenu
  return null;
}

export function Dropdown({
  overlay,
  trigger = ['click'],
  children,
  placement = 'bottomLeft',
  visible: controlledVisible,
  onVisibleChange,
  disabled = false,
  style,
}: DropdownProps) {
  const [internalVisible, setInternalVisible] = useState(false);
  const containerRef = useRef(null) as React.MutableRefObject<HTMLDivElement | null>;
  const menuRef = useRef(null) as React.MutableRefObject<HTMLDivElement | null>;

  const visible = controlledVisible !== undefined ? controlledVisible : internalVisible;

  const handleVisibleChange = (newVisible: boolean) => {
    if (controlledVisible === undefined) {
      setInternalVisible(newVisible);
    }
    onVisibleChange?.(newVisible);
  };

  // Handle click outside
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        handleVisibleChange(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible]);

  const getPlacementStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'absolute',
      zIndex: 1000,
      marginTop: '4px',
    };

    switch (placement) {
      case 'bottomLeft':
        return { ...base, left: 0 };
      case 'bottom':
        return { ...base, left: '50%', transform: 'translateX(-50%)' };
      case 'bottomRight':
        return { ...base, right: 0 };
      case 'topLeft':
        return { ...base, bottom: '100%', left: 0, marginBottom: '4px', marginTop: 0 };
      case 'top':
        return { ...base, bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '4px', marginTop: 0 };
      case 'topRight':
        return { ...base, bottom: '100%', right: 0, marginBottom: '4px', marginTop: 0 };
      default:
        return base;
    }
  };

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-block',
    ...style,
  };

  const overlayContent = Array.isArray(overlay) 
    ? React.createElement(DropdownMenu, { items: overlay })
    : overlay;

  return React.createElement('div', { ref: containerRef, style: containerStyle },
    React.cloneElement(children as any, {
      onClick: trigger.includes('click') ? () => !disabled && handleVisibleChange(!visible) : undefined,
      onMouseEnter: trigger.includes('hover') ? () => !disabled && handleVisibleChange(true) : undefined,
      onMouseLeave: trigger.includes('hover') ? () => handleVisibleChange(false) : undefined,
      onContextMenu: trigger.includes('contextMenu') ? (e: any) => {
        e.preventDefault();
        !disabled && handleVisibleChange(!visible);
      } : undefined,
    }),
    visible && React.createElement('div', {
      ref: menuRef,
      style: getPlacementStyle(),
    }, overlayContent)
  );
}

// Attach Menu and MenuItem to Dropdown
(Dropdown as any).Menu = DropdownMenu;
(Dropdown as any).MenuItem = DropdownMenuItem;

